import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import NewsCard from "./NewsCard";
import FooterCard from "./FooterCard";

const SWIPE_THRESHOLD_DISTANCE = 90;
const SWIPE_THRESHOLD_VELOCITY = 400;

export default function Deck({ articles, settings }) {
  const [index, setIndex] = useState(0);
  const total = articles.length + 1; // + footer
  const lockRef = useRef(false);

  // Reset index if the deck shrinks below the current position
  useEffect(() => {
    if (index > total - 1) setIndex(Math.max(0, total - 1));
  }, [total, index]);

  const advance = useCallback(
    (dir) => {
      if (lockRef.current) return;
      lockRef.current = true;
      setIndex((i) => {
        if (dir > 0) return Math.min(i + 1, total - 1);
        return Math.max(i - 1, 0);
      });
      setTimeout(() => (lockRef.current = false), 340);
    },
    [total]
  );

  const next = useCallback(() => advance(1), [advance]);
  const prev = useCallback(() => advance(-1), [advance]);

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowUp" || e.key === "PageUp") {
        e.preventDefault(); prev();
      } else if (e.key === "ArrowDown" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault(); next();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  // Wheel
  useEffect(() => {
    let cooldown = false;
    const onWheel = (e) => {
      if (cooldown) return;
      if (Math.abs(e.deltaY) < 20) return;
      if (e.deltaY > 0) next();
      else prev();
      cooldown = true;
      setTimeout(() => (cooldown = false), 520);
    };
    window.addEventListener("wheel", onWheel, { passive: true });
    return () => window.removeEventListener("wheel", onWheel);
  }, [next, prev]);

  const handleDragEnd = (_e, info) => {
    if (info.offset.y < -SWIPE_THRESHOLD_DISTANCE || info.velocity.y < -SWIPE_THRESHOLD_VELOCITY) {
      next();
    } else if (info.offset.y > SWIPE_THRESHOLD_DISTANCE || info.velocity.y > SWIPE_THRESHOLD_VELOCITY) {
      prev();
    }
  };

  const items = [
    ...articles.map((a, i) => ({ kind: "article", id: a.id, idx: i, article: a })),
    { kind: "footer", id: "footer", idx: articles.length },
  ];

  return (
    <div
      data-testid="deck-container"
      className="relative w-full h-[100dvh] overflow-hidden touch-none select-none bg-black"
    >
      <AnimatePresence initial={false}>
        {items.map((item) => {
          const depth = item.idx - index;
          if (depth < -1 || depth > 2) return null;

          const isTop = depth === 0;
          // No y-offset — pure scale stack, eliminates the visible peek
          const scale = depth <= 0 ? 1 : 1 - depth * 0.05;
          const opacity = depth < 0 ? 0 : depth > 1 ? 0 : 1;
          const rest = { y: 0, scale, opacity };
          // Exited cards (depth<0) must sit BELOW the new top card so they don't block touch
          const zIndex = depth < 0 ? 1 : 50 - depth;

          const commonProps = {
            depth,
            dragHandlers: isTop
              ? { onDragEnd: handleDragEnd, dragSnapToOrigin: true }
              : {},
            // pointer-events only on the active card → exit cards never trap touch on mobile
            style: { zIndex, pointerEvents: isTop ? "auto" : "none" },
            // initial matches resting state — newly-added cards appear in place, no slide
            initial: rest,
            animate: rest,
            exit: { y: "-110%", opacity: 0, scale: 1 },
            transition: { type: "spring", stiffness: 320, damping: 34, mass: 0.85 },
          };

          if (item.kind === "article") {
            return (
              <NewsCard key={item.id} article={item.article} {...commonProps} />
            );
          }
          return (
            <FooterCard
              key={item.id}
              settings={settings}
              onReset={() => setIndex(0)}
              {...commonProps}
            />
          );
        })}
      </AnimatePresence>

      <div
        data-testid="deck-progress"
        className="hidden sm:flex fixed left-6 sm:left-10 top-1/2 -translate-y-1/2 z-40 flex-col gap-1.5"
      >
        {items.map((_, i) => (
          <button
            key={i}
            data-testid={`deck-dot-${i}`}
            onClick={() => setIndex(i)}
            aria-label={`Go to card ${i + 1}`}
            className={`w-px transition-all duration-500 ${i === index ? "h-8 bg-white" : "h-3 bg-white/40 hover:bg-white/70"}`}
          />
        ))}
      </div>
    </div>
  );
}
